"use client";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import { FaCookieBite } from "react-icons/fa6";

const STORAGE_KEY = "cookiesAccepted";
export const CONSENT_EVENT = "cookie-consent-changed";
export const OPEN_SETTINGS_EVENT = "open-cookie-settings";

// Für andere Komponenten (z. B. Google-Karte): hat der Besucher zugestimmt?
export const hasCookieConsent = () =>
  typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "true";

// Banner erneut öffnen, z. B. über einen Link "Cookie-Einstellungen" im Footer
export const openCookieSettings = () =>
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));

const CookieBanner: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY) === null) setIsVisible(true);

    const open = () => setIsVisible(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, open);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, open);
  }, []);

  const saveChoice = (accepted: boolean) => {
    localStorage.setItem(STORAGE_KEY, String(accepted));
    window.dispatchEvent(new Event(CONSENT_EVENT));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-text"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:inset-x-auto sm:bottom-6 sm:left-6 sm:max-w-md sm:p-0"
    >
      <div className="flex flex-col gap-5 rounded-2xl border border-ao-line bg-ao-surface p-6 text-ao-fg shadow-2xl">
        <div className="flex items-start gap-4">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ao-surface-2 text-xl text-ao-accent-fg">
            <FaCookieBite />
          </span>
          <div className="flex flex-col gap-1.5">
            <h2
              id="cookie-title"
              className="font-display text-2xl font-bold uppercase leading-none"
            >
              Cookies
            </h2>
            <p
              id="cookie-text"
              className="text-sm leading-relaxed text-ao-muted"
            >
              Wir verwenden notwendige Cookies, damit die Website funktioniert.
              Mit Ihrer Zustimmung laden wir zusätzlich externe Inhalte wie
              Google Maps. Ihre Auswahl können Sie jederzeit über
              „Cookie-Einstellungen“ im Seitenfuß ändern. Mehr in der{" "}
              <Link
                href="/datenschutz"
                className="font-semibold text-ao-fg underline"
              >
                Datenschutzerklärung
              </Link>
              .
            </p>
          </div>
        </div>

        {/* Beide Knöpfe gleich groß – so verlangt es das Datenschutzrecht */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => saveChoice(false)}
            className="flex h-12 items-center justify-center rounded-xl border border-ao-line-strong text-sm font-semibold transition-colors hover:border-ao-accent"
          >
            Nur notwendige
          </button>
          <button
            type="button"
            onClick={() => saveChoice(true)}
            className="flex h-12 items-center justify-center rounded-xl bg-ao-accent text-sm font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
